import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { SeSecurityStore } from "./se.security.store";
import { SeConnect25ThemeStore } from "./se.connect25.theme.store";
import { PreferenceService } from "../../services/preference.service";
import { S25FilestoreImageComponent } from "../../standalone/s25.filestore.image.component";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25Util } from "../../util/s25-util";
import { FILE_KEY_CONNECT25_BANNER_LOGO, PREF_25L_CONNECT25_BRANDING } from "./se.connect25.constants";
import { DEFAULT_THEME_BRANDING, serializeThemeBranding, type ThemeBranding } from "./theme-branding";

@TypeManagerDecorator("s25-ng-se-connect25-branding-admin")
@Component({
    selector: "s25-ng-se-connect25-branding-admin",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25FilestoreImageComponent, S25ButtonComponent],
    template: `
        <h2>Connect25 appearance</h2>
        @if (canEdit()) {
            @if (themeStore.isLoading() && !themeStore.loaded()) {
                <p class="ngFinePrint">Loading…</p>
            } @else if (themeStore.error()) {
                <p class="fs-image__error" role="alert">
                    {{ themeStore.error()?.message || "Could not load branding." }}
                </p>
            } @else {
                <div class="se-branding__grid">
                    <label class="se-branding__field">
                        <span class="se-branding__label">Banner title</span>
                        <input
                            type="text"
                            class="se-branding__input"
                            [ngModel]="productTitle()"
                            (ngModelChange)="productTitle.set($event)"
                        />
                    </label>
                    <label class="se-branding__field">
                        <span class="se-branding__label">Banner background</span>
                        <div class="se-branding__color-row">
                            <input
                                type="color"
                                class="se-branding__color-swatch"
                                [ngModel]="
                                    S25Util.hexForColorPicker(bannerBackground(), themeDefaults.bannerBackground)
                                "
                                (ngModelChange)="
                                    bannerBackground.set(
                                        S25Util.hexForColorPicker($event, themeDefaults.bannerBackground)
                                    )
                                "
                                title="Pick banner background"
                                aria-label="Pick banner background color"
                            />
                            <input
                                type="text"
                                class="se-branding__input se-branding__input--hex"
                                [ngModel]="bannerBackground()"
                                (ngModelChange)="bannerBackground.set($event)"
                                placeholder="#46587c"
                            />
                        </div>
                    </label>
                    <label class="se-branding__field">
                        <span class="se-branding__label">Banner accent (bottom border)</span>
                        <div class="se-branding__color-row">
                            <input
                                type="color"
                                class="se-branding__color-swatch"
                                [ngModel]="S25Util.hexForColorPicker(bannerAccent(), themeDefaults.bannerAccent)"
                                (ngModelChange)="
                                    bannerAccent.set(S25Util.hexForColorPicker($event, themeDefaults.bannerAccent))
                                "
                                title="Pick banner accent"
                                aria-label="Pick banner accent color"
                            />
                            <input
                                type="text"
                                class="se-branding__input se-branding__input--hex"
                                [ngModel]="bannerAccent()"
                                (ngModelChange)="bannerAccent.set($event)"
                                placeholder="#04345d"
                            />
                        </div>
                    </label>
                    <label class="se-branding__field">
                        <span class="se-branding__label">Banner foreground (text)</span>
                        <div class="se-branding__color-row">
                            <input
                                type="color"
                                class="se-branding__color-swatch"
                                [ngModel]="
                                    S25Util.hexForColorPicker(bannerForeground(), themeDefaults.bannerForeground)
                                "
                                (ngModelChange)="
                                    bannerForeground.set(
                                        S25Util.hexForColorPicker($event, themeDefaults.bannerForeground)
                                    )
                                "
                                title="Pick banner text color"
                                aria-label="Pick banner foreground color"
                            />
                            <input
                                type="text"
                                class="se-branding__input se-branding__input--hex"
                                [ngModel]="bannerForeground()"
                                (ngModelChange)="bannerForeground.set($event)"
                                placeholder="#ffffff"
                            />
                        </div>
                    </label>
                    <label class="se-branding__field">
                        <span class="se-branding__label">Home slideshow gradient (start)</span>
                        <div class="se-branding__color-row">
                            <input
                                type="color"
                                class="se-branding__color-swatch"
                                [ngModel]="
                                    S25Util.hexForColorPicker(
                                        slideshowGradientStart(),
                                        themeDefaults.slideshowGradientStart
                                    )
                                "
                                (ngModelChange)="
                                    slideshowGradientStart.set(
                                        S25Util.hexForColorPicker($event, themeDefaults.slideshowGradientStart)
                                    )
                                "
                                title="Pick slideshow gradient start"
                                aria-label="Pick home slideshow gradient start color"
                            />
                            <input
                                type="text"
                                class="se-branding__input se-branding__input--hex"
                                [ngModel]="slideshowGradientStart()"
                                (ngModelChange)="slideshowGradientStart.set($event)"
                                placeholder="#4a5568"
                            />
                        </div>
                    </label>
                    <label class="se-branding__field">
                        <span class="se-branding__label">Home slideshow gradient (end)</span>
                        <div class="se-branding__color-row">
                            <input
                                type="color"
                                class="se-branding__color-swatch"
                                [ngModel]="
                                    S25Util.hexForColorPicker(
                                        slideshowGradientEnd(),
                                        themeDefaults.slideshowGradientEnd
                                    )
                                "
                                (ngModelChange)="
                                    slideshowGradientEnd.set(
                                        S25Util.hexForColorPicker($event, themeDefaults.slideshowGradientEnd)
                                    )
                                "
                                title="Pick slideshow gradient end"
                                aria-label="Pick home slideshow gradient end color"
                            />
                            <input
                                type="text"
                                class="se-branding__input se-branding__input--hex"
                                [ngModel]="slideshowGradientEnd()"
                                (ngModelChange)="slideshowGradientEnd.set($event)"
                                placeholder="#2d3748"
                            />
                        </div>
                    </label>
                </div>

                <label class="se-branding__field c-margin-top--single">
                    <span class="se-branding__label">Support email</span>
                    <input
                        type="email"
                        class="se-branding__input"
                        [ngModel]="supportEmail()"
                        (ngModelChange)="supportEmail.set($event)"
                        placeholder="support@school.edu"
                        aria-describedby="se-branding-support-email-help"
                    />
                    <span id="se-branding-support-email-help" class="ngFinePrint se-branding__help">
                        Shown to Connect25 users as a "Contact Support" link. Leave blank to hide it.
                    </span>
                </label>

                <div class="se-branding__logo c-margin-top--single">
                    <span class="se-branding__label">Banner logo</span>
                    <s25-ng-filestore-image
                        [value]="logoFileRef()"
                        (valueChange)="logoFileRef.set($event)"
                        [objectType]="-1"
                        [objectId]="-1"
                        [fileKey]="fileKey"
                        [saveButton]="false"
                    />
                </div>

                <div class="se-branding__actions c-margin-top--single">
                    <s25-ng-button [type]="'primary'" [buttonText]="'Save'" [onClick]="save" [disabled]="saving()" />
                </div>
                @if (saveError()) {
                    <p class="fs-image__error c-margin-top--single" role="alert">{{ saveError() }}</p>
                }
                @if (saveSuccess()) {
                    <p class="fs-image__success c-margin-top--single" role="status">{{ saveSuccess() }}</p>
                }
            }
        } @else {
            <p class="ngFinePrint">You need full Connect25 access to edit branding.</p>
        }
    `,
    styles: `
        :host {
            display: block;
        }
        .se-branding__grid {
            display: grid;
            gap: 0.75rem 1.25rem;
            grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
            max-width: 720px;
        }
        .se-branding__field--wide {
            grid-column: 1 / -1;
        }
        .se-branding__label {
            display: block;
            font-size: 0.875rem;
            font-weight: 500;
            margin-bottom: 0.25rem;
        }
        .se-branding__help {
            display: block;
            margin-top: 0.25rem;
        }
        .se-branding__input {
            width: 100%;
            max-width: 320px;
            padding: 0.35rem 0.5rem;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 0.875rem;
        }
        .se-branding__color-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            max-width: 320px;
        }
        .se-branding__color-swatch {
            width: 2.25rem;
            height: 2.25rem;
            padding: 0;
            border: 1px solid #ccc;
            border-radius: 4px;
            flex-shrink: 0;
            cursor: pointer;
            box-sizing: border-box;
        }
        .se-branding__input--hex {
            flex: 1;
            min-width: 0;
            max-width: none;
        }
        .se-branding__logo .se-branding__label {
            margin-bottom: 0.5rem;
        }
        .fs-image__error {
            color: #c62828;
            font-size: 0.85rem;
            margin: 0;
        }
        .fs-image__success {
            color: #2e7d32;
            font-size: 0.85rem;
            margin: 0;
        }
    `,
})
export class SeConnect25BrandingAdminComponent {
    readonly S25Util = S25Util;

    readonly themeStore = inject(SeConnect25ThemeStore);
    private readonly securityStore = inject(SeSecurityStore);

    private readonly logoFilestore = viewChild(S25FilestoreImageComponent);

    /** Defaults for color-picker fallback when hex is invalid or shorthand. */
    readonly themeDefaults = DEFAULT_THEME_BRANDING;

    readonly prefName = PREF_25L_CONNECT25_BRANDING;
    readonly fileKey = FILE_KEY_CONNECT25_BANNER_LOGO;

    readonly canEdit = computed(() => this.securityStore.hasFLSOverride());

    readonly productTitle = signal("");
    readonly logoFileRef = signal("");
    readonly bannerBackground = signal("");
    readonly bannerAccent = signal("");
    readonly bannerForeground = signal("");
    readonly slideshowGradientStart = signal("");
    readonly slideshowGradientEnd = signal("");
    readonly supportEmail = linkedSignal(() => this.themeStore.loadedValue()?.supportEmail ?? "");

    readonly saving = signal(false);
    readonly saveError = signal("");
    readonly saveSuccess = signal("");

    constructor() {
        effect(() => {
            void this.securityStore.ensureInitialized();
        });

        effect(() => {
            if (!this.canEdit()) return;
            const b = this.themeStore.loadedValue();
            if (!b) return;
            this.productTitle.set(b.productTitle);
            this.logoFileRef.set(b.logoFileRef);
            this.bannerBackground.set(b.bannerBackground);
            this.bannerAccent.set(b.bannerAccent);
            this.bannerForeground.set(b.bannerForeground);
            this.slideshowGradientStart.set(b.slideshowGradientStart);
            this.slideshowGradientEnd.set(b.slideshowGradientEnd);
        });
    }

    save = async () => {
        this.saveError.set("");
        this.saveSuccess.set("");
        const supportEmail = this.supportEmail().trim();
        if (supportEmail && !S25Util.isValidEmail(supportEmail)) {
            this.saveError.set("Please enter a valid support email.");
            return;
        }
        this.saving.set(true);
        try {
            await this.logoFilestore()?.guardedSave();
            const fsErr = this.logoFilestore()?.errorMsg()?.trim();
            if (fsErr) {
                this.saveError.set(fsErr);
                return;
            }

            const payload: ThemeBranding = {
                productTitle: this.productTitle().trim(),
                logoFileRef: this.logoFileRef().trim(),
                bannerBackground: this.bannerBackground().trim(),
                bannerAccent: this.bannerAccent().trim(),
                bannerForeground: this.bannerForeground().trim(),
                slideshowGradientStart: this.slideshowGradientStart().trim(),
                slideshowGradientEnd: this.slideshowGradientEnd().trim(),
                supportEmail,
            };
            const json = serializeThemeBranding(payload);
            await PreferenceService.setPreference(PREF_25L_CONNECT25_BRANDING, json, "S");
            PreferenceService.invalidatePreferences([PREF_25L_CONNECT25_BRANDING], "S");
            this.saveSuccess.set("Saved.");
            this.themeStore.reload();
        } catch (e: unknown) {
            this.saveError.set(S25Util.errorText(e, "Failed to save."));
        } finally {
            this.saving.set(false);
        }
    };
}
